import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Container, Row, Col } from 'react-bootstrap';
import { ArrowLeft, Eye } from 'lucide-react';
import { toast } from 'react-toastify';
import { apiRequests } from '../../shared/api/apiRequests';
import { dateToString } from '../../shared/utils/parseDate';
import { Pagination } from '../../features/Pagination';
import { PageBar } from '../../shared/ui/PageBar';
import { EntityCard } from '../../shared/ui/EntityCard';

// Компонент для отображения заявок на вакансию
const VacancySummariesPage = observer(() => {
  const { id: vacancyId } = useParams(); // Получение ID вакансии из URL
  const [vacancy, setVacancy] = useState(null);
  const [summaries, setSummaries] = useState({ items: [], totalCount: 0 });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  // Статусы заявок с переводом и цветом
  const statusMap = {
    1: { name: 'Активная', color: 'primary' },
    2: { name: 'Кандидат', color: 'info' },
    3: { name: 'Интервью', color: 'warning' },
    4: { name: 'Нанят', color: 'success' },
    5: { name: 'Не нанят', color: 'danger' },
    6: { name: 'Резерв', color: 'secondary' },
    7: { name: 'Отклонен', color: 'dark' },
  };

  // Получение данных вакансии
  const getVacancyData = async () => {
    try {
      const vacancyData = await apiRequests.hr.getById(vacancyId);
      setVacancy(vacancyData);
    } catch (error) {
      console.error('Ошибка при получении данных вакансии:', error);
      toast.error('Не удалось загрузить информацию о вакансии');
    }
  };

  // Загрузка данных заявок
  const loadSummaries = async () => {
    if (loading) return; // Предотвращаем повторные запросы
    
    setLoading(true);
    
    try {
      // Формируем параметры запроса
      const params = {
        vacancyId,
        count: 10,
        offset: currentPage * 10
      };
      
      // Добавляем поиск
      if (searchQuery) {
        params.query = searchQuery;
      }
      
      // Добавляем статус
      if (selectedStatus) {
        params.status = selectedStatus;
      }
      
      console.log('Запрос заявок с параметрами:', params);
      
      // Запрос к API
      const response = await apiRequests.hr.getSummaries(
        params.vacancyId,
        params.status,
        params.query, 
        params.count,
        params.offset
      );
      
      setSummaries({
        items: response.items || [],
        totalCount: response.totalCount || 0
      });
    } catch (error) {
      console.error('Ошибка при получении заявок:', error);
      toast.error('Не удалось загрузить заявки');
      setSummaries({ items: [], totalCount: 0 });
    } finally {
      setLoading(false);
    }
  };

  // Обработчик поиска
  const handleSearch = () => {
    setCurrentPage(0);
    loadSummaries(); 
  };

  // Обработчик изменения фильтра
  const handleFilterChange = (key, value) => {
    if (key === 'status') {
      const intValue = value === null ? null : parseInt(value);
      setSelectedStatus(intValue);
    }
  };

  // Применить фильтры
  const applyFilters = () => {
    setCurrentPage(0);
    loadSummaries();
  };

  // Сбросить фильтры
  const resetFilters = () => {
    setSelectedStatus(null);
    setSearchQuery('');
    setCurrentPage(0);
    loadSummaries();
  };

  // Обработчик пагинации
  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadSummaries();
  };

  // При изменении параметров поиска или фильтрации
  useEffect(() => {
    loadSummaries();
  }, [currentPage, searchQuery, selectedStatus]);

  // При первой загрузке
  useEffect(() => {
    getVacancyData();
  }, [vacancyId])

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="mb-0">
              Заявки на вакансию {vacancy ? <span className="text-primary">"{vacancy.title}"</span> : ''}
            </h2>
          </div>
          
          <PageBar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            onSearch={handleSearch}
            searchPlaceholder="Поиск по имени или информации..."
            filters={[
              {
                key: 'status',
                type: 'select',
                label: 'Статус',
                value: selectedStatus === null ? null : String(selectedStatus),
                placeholder: 'Все статусы',
                options: Object.entries(statusMap).map(([key, value]) => ({ value: key, label: value.name })),
              },
            ]}
            onFilterChange={handleFilterChange}
            onApplyFilters={applyFilters}
            onResetFilters={resetFilters}
            rightContent={
              <Link to={`/HR/vacancies/${vacancyId}`} className="page-bar-clean__link-btn">
                <ArrowLeft size={18} />
                <span>К вакансии</span>
              </Link>
            }
          />
        </Col>
      </Row>

      {/* Список заявок */}
      <Row>
        {loading ? (
          <Col className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Загрузка...</span>
            </div>
          </Col>
        ) : summaries.items.length === 0 ? (
          <Col className="text-center py-5">
            <p>По вашему запросу не найдено заявок</p>
          </Col>
        ) : (
          <>
            <Col xs={12}>
              <div className="mb-3">
                {summaries.items.map((summary) => {
                  const statusInfo = statusMap[summary.status] || { name: 'Активная', color: 'primary' };
                  return (
                    <EntityCard
                      key={summary.id}
                      image={`https://ui-avatars.com/api/?name=${summary.name?.[0] || 'U'}&color=7F9CF5&background=EBF4FF`}
                      title={`${summary.name} ${summary.surname}`}
                      subtitle={summary.aboutInfo || 'Без описания'}
                      badge={{ text: statusInfo.name, variant: statusInfo.color }}
                      fields={[
                        { label: 'Создано', value: dateToString(summary.createdAt) },
                      ]}
                      actions={
                        <Link
                          to={`/HR/vacancies/${vacancyId}/summaries/${summary.id}`}
                          className="entity-card__action"
                          title="Подробнее"
                        >
                          <Eye size={20} />
                        </Link>
                      }
                    />
                  );
                })}
              </div>
            </Col>
            
            <Col xs={12} className="mt-4">
              <Pagination
                count={summaries.totalCount || 0}
                itemsPerPage={10}
                noRequest={true}
                onChange={(pageIndex) => handlePageChange(pageIndex)}
              />
            </Col>
          </>
        )}
      </Row>
    </Container>
  );
});

export { VacancySummariesPage };
